import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetRecentActivityQuery } from 'store/services/activity/activityApi';
import { useActivitySocket } from './useActivitySocket';
import { Activity } from 'types/interfaces/activity';

// Items per page based on viewport
export const ITEMS_PER_PAGE_DESKTOP = 6;
export const ITEMS_PER_PAGE_TABLET = 4;
export const ITEMS_PER_PAGE_MOBILE = 2;

interface UseActivityCarouselOptions {
  /** Enable WebSocket connection for real-time updates */
  enableSocket?: boolean;
  /** Enable polling fallback when socket is disconnected */
  enablePolling?: boolean;
  /** Polling interval in ms when socket is disconnected (default: 60000) */
  pollingInterval?: number;
}

interface UseActivityCarouselReturn {
  /** All activities from the API */
  activities: Activity[];
  /** Activities visible on the current page */
  visibleActivities: Activity[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Error from the API */
  error: any;
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Items shown per page (based on viewport) */
  itemsPerPage: number;
  /** Whether there is a previous page */
  hasPrevPage: boolean;
  /** Whether there is a next page */
  hasNextPage: boolean;
  /** Go to a specific page */
  goToPage: (page: number) => void;
  /** Go to the next page */
  goNext: () => void;
  /** Go to the previous page */
  goPrevious: () => void;
  /** Refetch the activity data */
  refetch: () => void;
  /** Whether the WebSocket is connected */
  isSocketConnected: boolean;
}

/**
 * Hook to manage the activity carousel state
 * Combines RTK Query for initial data with Socket.IO for real-time updates
 */
export const useActivityCarousel = (
  options: UseActivityCarouselOptions = {}
): UseActivityCarouselReturn => {
  const {
    enableSocket = true,
    enablePolling = true,
    pollingInterval = 60000,
  } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);

  // Get socket connection state
  const isSocketConnected = useSelector(
    (state: RootState) => state.activity.isConnected
  );

  // Initialize socket connection
  useActivitySocket({ enabled: enableSocket });

  // Fetch activity data
  // Only poll if socket is disconnected and polling is enabled
  const shouldPoll = enablePolling && !isSocketConnected;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetRecentActivityQuery(
    { limit: 54 },
    {
      pollingInterval: shouldPoll ? pollingInterval : 0,
      refetchOnMountOrArgChange: true,
    }
  );

  // Handle window resize for responsive items per page
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setItemsPerPage(ITEMS_PER_PAGE_MOBILE);
      } else if (width <= 1024) {
        setItemsPerPage(ITEMS_PER_PAGE_TABLET);
      } else {
        setItemsPerPage(ITEMS_PER_PAGE_DESKTOP);
      }
    };

    // Set initial value
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate pagination values
  const activities = useMemo(() => data?.activities ?? [], [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(activities.length / itemsPerPage)),
    [activities.length, itemsPerPage]
  );

  // Reset to page 1 if current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Get visible activities for current page
  const visibleActivities = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return activities.slice(startIndex, endIndex);
  }, [activities, currentPage, itemsPerPage]);

  // Navigation functions
  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  const goNext = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goPrevious = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  return {
    activities,
    visibleActivities,
    isLoading,
    error,
    currentPage,
    totalPages,
    itemsPerPage,
    hasPrevPage: currentPage > 1,
    hasNextPage: currentPage < totalPages,
    goToPage,
    goNext,
    goPrevious,
    refetch,
    isSocketConnected,
  };
};

export default useActivityCarousel;
